/**
 * FAQs Search Functionality
 * - Searches in accordion titles and content starting from 3 characters
 * - Highlights matching text with <mark> tags
 * - Opens accordions if match is found in content
 * - Shows only relevant accordions and their group headings
 * - Filters chips to show only groups with results
 * - Shows "no results" message when nothing is found
 */

class FaqsSearch {
  constructor() {
    // DOM elements
    this.searchInput = document.getElementById('faqs-search-input');
    this.resetButton = this.searchInput?.closest('.field')?.querySelector('.reset__button');
    this.accordions = document.querySelectorAll('arn-accordion');
    this.headings = document.querySelectorAll('[id^="faq-heading-"]');
    this.chipsContainer = document.getElementById('arn-search-chips-container');
    this.chips = this.chipsContainer?.querySelectorAll('a') || [];

    // Store original content to restore after search
    this.originalContent = new Map();

    this.init();
  }

  init() {
    if (!this.searchInput) return;

    // Store original content for each accordion (to restore after highlighting)
    this.accordions.forEach((accordion, index) => {
      const title = accordion.querySelector('summary span');
      const content = accordion.querySelector('.arn-accordion-content > div');

      this.originalContent.set(index, {
        title: title?.innerHTML || '',
        content: content?.innerHTML || ''
      });
    });

    // Event listeners
    this.searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));
    this.resetButton?.addEventListener('click', () => this.resetSearch());
  }

  handleSearch(query) {
    const searchTerm = query.trim();

    // Show/hide reset button based on input value
    if (this.resetButton) {
      if (searchTerm.length > 0) {
        this.resetButton.classList.remove('hidden');
      } else {
        this.resetButton.classList.add('hidden');
      }
    }

    // Don't search if less than 3 characters
    if (searchTerm.length < 3) {
      this.showAll();
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    let hasResults = false;
    const visibleGroupIndices = new Set();

    // Search in each accordion
    this.accordions.forEach((accordion, index) => {
      const details = accordion.querySelector('details');
      const titleElement = accordion.querySelector('summary span');
      const contentElement = accordion.querySelector('.arn-accordion-content > div');

      // Get original content
      const originalTitle = this.originalContent.get(index).title;
      const originalContent = this.originalContent.get(index).content;

      // Create temporary elements to extract text content
      const tempTitleDiv = document.createElement('div');
      tempTitleDiv.innerHTML = originalTitle;
      const titleText = tempTitleDiv.textContent || tempTitleDiv.innerText;

      const tempContentDiv = document.createElement('div');
      tempContentDiv.innerHTML = originalContent;
      const contentText = tempContentDiv.textContent || tempContentDiv.innerText;

      // Check for matches
      const titleMatches = titleText.toLowerCase().includes(searchTermLower);
      const contentMatches = contentText.toLowerCase().includes(searchTermLower);
      const hasMatch = titleMatches || contentMatches;

      if (hasMatch) {
        hasResults = true;

        // Show accordion parent wrapper
        accordion.parentElement.style.display = '';

        // Highlight matches in title
        if (titleMatches) {
          titleElement.innerHTML = this.highlightText(originalTitle, searchTerm);
        } else {
          titleElement.innerHTML = originalTitle;
        }

        // Highlight matches in content
        if (contentMatches) {
          contentElement.innerHTML = this.highlightText(originalContent, searchTerm);
          // Open accordion if match is in content
          details.setAttribute('open', '');
        } else {
          contentElement.innerHTML = originalContent;
          // Don't open if match is only in title
          if (!titleMatches) {
            details.removeAttribute('open');
          }
        }

        // Track which group this accordion belongs to
        const groupIndex = this.getGroupIndexForAccordion(accordion);
        if (groupIndex !== -1) {
          visibleGroupIndices.add(groupIndex);
        }
      } else {
        // Hide accordion parent wrapper
        accordion.parentElement.style.display = 'none';
        titleElement.innerHTML = originalTitle;
        contentElement.innerHTML = originalContent;
        details.removeAttribute('open');
      }
    });

    // Show/hide group headings based on visible accordions
    this.headings.forEach((heading, index) => {
      const shouldShow = visibleGroupIndices.has(index);
      heading.closest('div').style.display = shouldShow ? '' : 'none';
    });

    // Filter chips to show only relevant groups
    this.filterChips(visibleGroupIndices);

    // Show "no results" message if needed
    this.toggleNoResultsMessage(!hasResults);
  }

  /**
   * Highlights search term in text with <mark> tags
   * Case-insensitive search but preserves original case in result
   */
  highlightText(html, searchTerm) {
    // Create a temporary element to work with
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Function to highlight text in a node
    const highlightNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        const lowerText = text.toLowerCase();
        const lowerSearchTerm = searchTerm.toLowerCase();

        if (lowerText.includes(lowerSearchTerm)) {
          const fragment = document.createDocumentFragment();
          let lastIndex = 0;
          let index = lowerText.indexOf(lowerSearchTerm);

          while (index !== -1) {
            // Add text before match
            if (index > lastIndex) {
              fragment.appendChild(document.createTextNode(text.substring(lastIndex, index)));
            }

            // Add highlighted match
            const mark = document.createElement('mark');
            mark.className = 'tw-bg-arn-brand tw-text-inherit';
            mark.textContent = text.substring(index, index + searchTerm.length);
            fragment.appendChild(mark);

            lastIndex = index + searchTerm.length;
            index = lowerText.indexOf(lowerSearchTerm, lastIndex);
          }

          // Add remaining text
          if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
          }

          node.parentNode.replaceChild(fragment, node);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'MARK') {
        // Recursively process child nodes (skip if already a mark tag)
        Array.from(node.childNodes).forEach(child => highlightNode(child));
      }
    };

    highlightNode(temp);
    return temp.innerHTML;
  }

  /**
   * Gets the group index (heading index) for a given accordion
   * Returns -1 if no heading found
   */
  getGroupIndexForAccordion(accordion) {
    let currentElement = accordion.parentElement;

    // Walk up the DOM to find the previous heading
    while (currentElement) {
      const heading = currentElement.querySelector('[id^="faq-heading-"]');
      if (heading) {
        // Extract index from id (faq-heading-1 -> 0, faq-heading-2 -> 1, etc.)
        const match = heading.id.match(/faq-heading-(\d+)/);
        if (match) {
          return parseInt(match[1]) - 1; // Convert to 0-based index
        }
      }
      currentElement = currentElement.previousElementSibling;
    }

    return -1;
  }

  /**
   * Filters chips to show only groups with visible accordions
   */
  filterChips(visibleGroupIndices) {
    if (!this.chipsContainer) return;

    this.chips.forEach((chip, index) => {
      const shouldShow = visibleGroupIndices.has(index);
      chip.style.display = shouldShow ? '' : 'none';
    });
  }

  /**
   * Shows all accordions, headings, and chips (reset state)
   */
  showAll() {
    // Restore all accordions
    this.accordions.forEach((accordion, index) => {
      accordion.parentElement.style.display = '';

      // Restore original content without highlighting
      const titleElement = accordion.querySelector('summary span');
      const contentElement = accordion.querySelector('.arn-accordion-content > div');

      if (titleElement) {
        titleElement.innerHTML = this.originalContent.get(index).title;
      }
      if (contentElement) {
        contentElement.innerHTML = this.originalContent.get(index).content;
      }

      // Close accordion
      accordion.querySelector('details')?.removeAttribute('open');
    });

    // Show all headings
    this.headings.forEach((heading) => {
      heading.closest('div').style.display = '';
    });

    // Show all chips
    this.chips.forEach((chip) => {
      chip.style.display = '';
    });

    // Hide "no results" message
    this.toggleNoResultsMessage(false);
  }

  /**
   * Resets the search input and shows all content
   */
  resetSearch() {
    this.searchInput.value = '';

    // Hide reset button
    if (this.resetButton) {
      this.resetButton.classList.add('hidden');
    }

    this.showAll();
  }

  /**
   * Shows or hides the "no results found" message
   */
  toggleNoResultsMessage(show) {
    const noResultsMsg = document.getElementById('no-results-message');
    const separator = document.getElementById('faqs-separator');

    if (!noResultsMsg) return;

    if (show) {
      noResultsMsg.classList.remove('tw-hidden');
      separator?.classList.add('tw-hidden');
    } else {
      noResultsMsg.classList.add('tw-hidden');
      separator?.classList.remove('tw-hidden');
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new FaqsSearch());
} else {
  new FaqsSearch();
}